import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, GoogleLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="grid min-h-screen lg:grid-cols-[1fr_1.05fr]">
    <div className="hidden flex-col justify-between border-r border-slate-200 bg-navy-900 p-12 lg:flex">
      <Link to="/" className="flex items-center gap-2.5">
        <ShieldCheck size={26} weight="duotone" className="text-blue-300" />
        <span className="font-display text-[17px] font-extrabold text-white">ProductSecurityReady</span>
      </Link>
      <div>
        <h2 className="max-w-md font-display text-3xl font-bold leading-tight text-white">
          Indicative readiness across CRA, IEC 62443-4-1 and 4-2, EU MR, UKCA and UK PSTI, scored
          deterministically.
        </h2>
        <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-300">
          Product profile in, applicability guidance out. Then a guided assessment your engineering
          team will recognise: firmware integrity, remote access, SBOM, interfaces, lifecycle.
        </p>
      </div>
      <p className="max-w-md text-xs leading-relaxed text-slate-400">
        Outputs are indicative and are not certification, legal advice, or a statement of compliance.
      </p>
    </div>

    <div className="flex items-center justify-center px-5 py-14 lg:px-16">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center gap-2 lg:hidden">
          <ShieldCheck size={22} weight="duotone" className="text-navy-800" />
          <span className="font-display text-base font-extrabold text-navy-900">ProductSecurityReady</span>
        </Link>
        <h1 className="font-display text-3xl font-extrabold text-navy-900">{title}</h1>
        <p className="mt-2 text-sm text-slate-600">{subtitle}</p>
        <div className="mt-8">{children}</div>
        {footer && <div className="mt-6 text-sm text-slate-600">{footer}</div>}
      </div>
    </div>
  </div>
);

export const GoogleButton = ({ label }) => (
  <Button
    type="button"
    variant="outline"
    className="w-full"
    data-testid="google-login-button"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
  >
    <GoogleLogo size={18} className="mr-2" /> {label}
  </Button>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(form.email, form.password);
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Log in"
      subtitle="Access your product dashboard and assessments."
      footer={
        <>
          No account?{" "}
          <Link to="/register" className="font-medium text-navy-800 underline underline-offset-4" data-testid="link-register">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-xs">Work email</Label>
          <Input
            id="email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="login-email-input"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs">Password</Label>
          <Input
            id="password"
            type="password"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="login-password-input"
          />
        </div>
        {error && (
          <p className="border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="login-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={busy}
          className="w-full bg-navy-800 text-white hover:bg-navy-700"
          data-testid="login-submit-button"
        >
          {busy ? "Signing in…" : "Log in"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label="Continue with Google" />
      <Link
        to="/forgot-password"
        className="mt-5 block text-sm text-slate-600 underline underline-offset-4 hover:text-navy-800"
        data-testid="link-forgot-password"
      >
        Forgot your password?
      </Link>
    </AuthShell>
  );
}
